import {
  AppConfigurationClient,
  featureFlagPrefix,
  parseFeatureFlag,
} from "@azure/app-configuration";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export class Dictionary<T> {
  [index: string]: T;
}

interface ConfigContextProps {
  config: Dictionary<string> | undefined;
  setConfig: Dispatch<SetStateAction<Dictionary<string> | undefined>>;
  featureFlags: Dictionary<(firmId?: string) => boolean> | undefined;
  setFeatureFlags: Dispatch<
    SetStateAction<Dictionary<(firmId?: string) => boolean> | undefined>
  >;
}

interface Config {
  ApiUrl?: string;
  [key: string]: string | undefined;
}

const ENVIRONMENT: string = import.meta.env.VITE_ENVIRONMENT ?? "";

const CONNECTION_STRING =
  "Endpoint=https://bp-public-config.azconfig.io;Id=YGfk;Secret=7+e+EOAwn5qzNxKtQMk8h/GltRwikTkPYnrNgUsqnfg=";

// Module-level flag to prevent concurrent API calls
let isLoadingConfig = false;

const ConfigContext = createContext<ConfigContextProps | null>(null);

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<Dictionary<string> | undefined>();
  const [featureFlags, setFeatureFlags] = useState<
    Dictionary<(firmId?: string) => boolean> | undefined
  >();

  const ctxValues = useMemo<ConfigContextProps>(
    () => ({ config, setConfig, featureFlags, setFeatureFlags }),
    [config, featureFlags],
  );

  return <ConfigContext value={ctxValues}>{children}</ConfigContext>;
};

function useConfigContext(): ConfigContextProps {
  const ctx = use(ConfigContext);
  if (ctx === null) {
    throw new Error("Config context is null");
  }
  return ctx;
}

/**
 * Provides access to remote app configuration and feature flags loaded from Azure App Configuration.
 *
 * Config is loaded once on mount and shared across all consumers via context.
 *
 * **`getConfig()`** — returns key/value app settings (e.g. `ApiUrl`).
 *
 * **`getFeatureFlags()`** — returns only the flags that are defined in Azure. A flag that
 * does not exist in Azure will not be present in the returned dictionary. Always call
 * the result conditionally:
 *
 * ```ts
 * const flags = getFeatureFlags();
 * const isEnabled = flags[CLIENT_UPLOAD_FF]?.(firmId) ?? false;
 * //                                         ^ may be undefined if not defined in Azure
 * ```
 *
 * Flag constants are defined in `src/lib/feature-flags.ts`.
 */
export default function useConfig(): {
  config: Config | null;
  getConfig: () => Config;
  getFeatureFlags: () => Dictionary<(firmId?: string) => boolean>;
} {
  const {
    config: rawConfig,
    setConfig,
    featureFlags,
    setFeatureFlags,
  } = useConfigContext();
  const config: Config | null = rawConfig ? (rawConfig as Config) : null;

  useEffect(() => {
    if (config || isLoadingConfig) {
      return;
    }

    isLoadingConfig = true;

    const loadConfig = async () => {
      try {
        // READONLY CONNECTIONSTRING ONLY!
        const client = new AppConfigurationClient(CONNECTION_STRING);

        const configs = client.listConfigurationSettings({
          labelFilter: ENVIRONMENT,
        });

        const configSettings = new Dictionary<string>();
        const featureFlagSettings: Dictionary<(firmId?: string) => boolean> =
          {};

        for await (const setting of configs) {
          const val =
            setting.value !== undefined
              ? setting
              : await client.getConfigurationSetting({
                  key: setting.key,
                  label: ENVIRONMENT,
                });

          if (val.key.startsWith(featureFlagPrefix) && val.value) {
            const flag = parseFeatureFlag(val);

            if (!flag.value.id) {
              continue;
            }

            if (flag.value.conditions?.clientFilters?.length) {
              const firmFilter = flag.value.conditions.clientFilters.find(
                (x) => x.name === "Firm",
              )?.parameters?.firmId;

              if (firmFilter) {
                featureFlagSettings[flag.value.id] = (firmId?: string) => {
                  if (firmId && (firmFilter as string).includes(firmId)) {
                    return true;
                  }
                  return false;
                };
                continue;
              }

              throw new Error(
                "I only know how to filter on Firms (with 1 param 'firmId' containing a comma separated list of enabled Firm IDs) at the moment.",
              );
            }
            featureFlagSettings[flag.value.id] = () => flag.value.enabled;
          } else if (val.value) {
            configSettings[setting.key] = val.value;
          }
        }
        setConfig(configSettings);
        setFeatureFlags(featureFlagSettings);
      } finally {
        isLoadingConfig = false;
      }
    };

    void loadConfig();
  }, [config, setConfig, setFeatureFlags]);

  const getConfig = useCallback((): Config => {
    return config ?? ({} as Config);
  }, [config]);

  const getFeatureFlags = useCallback(() => {
    return featureFlags ?? ({} as Dictionary<(firmId?: string) => boolean>);
  }, [featureFlags]);

  return { config, getConfig, getFeatureFlags };
}
